import React from 'react';
import { Box } from '@chakra-ui/react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ExperiencedHero from '../components/landing/ExperiencedHero';
import ExperiencedWhoFor from '../components/landing/ExperiencedWhoFor';
import ExperiencedCurriculum from '../components/landing/ExperiencedCurriculum';
import ExperiencedProjects from '../components/landing/ExperiencedProjects';
import ExperiencedTechStack from '../components/landing/ExperiencedTechStack';
import ExperiencedROI from '../components/landing/ExperiencedROI';
import ExperiencedSuccess from '../components/landing/ExperiencedSuccess';
import { InvestmentSection } from '../components/beginnerTrackComponents';
import ExperiencedSchedule from '../components/landing/ExperiencedSchedule';
import ExperiencedFAQ from '../components/landing/ExperiencedFAQ';
import ExperiencedCTA from '../components/landing/ExperiencedCTA';
import { useAppData } from '../store/dataStore';
import { CodeverseMentorSection } from '../components/CodeverseMentorSection';

// Import icons
import { FaCode, FaChartLine, FaUsers, FaRocket, FaCalendar, FaRobot, FaSearch, FaImage, FaServer, FaClock, FaVideo, FaLaptop } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
import { useAffiliateCode } from '../hooks/useAffiliateCode';

const ExperiencedTrackPage = () => {
  const { appData } = useAppData();
  const { studentsCount, course2 } = appData;

  // Initialize affiliate code tracking
  useAffiliateCode('experienced_track_visit');

  // Centralized data object using values from data store
  const pageData = {
    hero: {
      badgeText: `${studentsCount}+ Professionals Upskilled`,
      title: "Ship production-grade AI+Fullstack systems in 8 weeks",
      description:
        "You already code. We help you turn experience into real AI apps, deploy to cloud, and gain the confidence you’ve been missing.",
      ctaButton: {
        text: "Check Fit & Eligibility",
        href: "/assessment",
      }
    },

    whoFor: {
      badge: {
        text: "Fast-Track Program",
        bg: "accent.green",
        color: "white"
      },
      title: "Who This Is For",
      description: "Skip fundamentals. Learn how production AI is really built.",
      audiences: [
        { icon: FaCode, title: 'Software Developers', desc: 'Add LLMs and vector search to your stack' },
        { icon: FaChartLine, title: 'Legacy Engineers', desc: 'Move from CRUD systems to AI-powered automation' },
        { icon: FaUsers, title: 'Technical Leads', desc: 'Integrate GenAI in your team safely and reliably' },
        { icon: FaRocket, title: 'Builders', desc: 'You want to ship products, not read papers' },
      ]

    },

    curriculum: {
      title: "Fast-Track Curriculum",
      description: "Designed for developers who learn by building. Every week ends in a working artifact.",
      weeks: [
        {
          weeks: '2 Weeks',
          title: 'Foundation Reset',
          topics: ['Python Refresher', 'ML/DL fundamentals refresher', 'NumPy & Pandas optimization', 'Work on cutting edge scenarios'],
        },
        {
          weeks: '2 Weeks',
          title: 'LLM Mastery',
          topics: ['Prompt engineering', 'NLP Refresher', 'AI Based system', 'Langchain', 'Fine-tuning strategies'],
        },
        {
          weeks: '1 Week',
          title: 'AI Deepdive',
          topics: ['Vector databases & embeddings', 'RAG architecture', 'Multi-modal AI applications', 'Deliver a Chatbot'],
        },
        {
          weeks: '2 Weeks',
          title: 'Full stack',
          topics: ['Nicegui-python based UI', 'FastAPI', 'React Applications', 'RDBMS: Mysql', 'NoSQL: Firestore', 'Deliver a Fully working app'],
        },
        {
          weeks: '1 Week',
          title: 'Deployment & Scale',
          topics: ['Cloud', 'Monitoring & observability', 'Performance optimization', 'Develop and deploy complete app on Firebase'],
        },
      ]
    },

    projects: {
      title: "Advanced Project Portfolio",
      description: "5 production-grade projects with GitHub repos and live demos.",
      projects: [
        { icon: FaCode, title: 'Custom LLM Fine-tuning', desc: 'Fine-tune GPT models for domain-specific tasks', badge: 'Advanced' },
        { icon: FaRobot, title: 'RAG Chatbot', desc: 'Vector search-powered conversational AI', badge: 'Real-world' },
        { icon: FaImage, title: 'Multi-modal AI App', desc: 'Text + image processing application', badge: 'Cutting-edge' },
        { icon: FaServer, title: 'Deployed API', desc: 'Scalable API with monitoring & analytics', badge: 'Advanced' },
        { icon: FaSearch, title: 'Semantic Search Engine', desc: 'Embedding-based search system', badge: 'Real-world' },
      ]
    },

    techStack: {
      title: "Technical Deep-Dive",
      description: "Master the modern GenAI tech stack used by leading companies.",
      technologies: [
        { name: 'LangChain', category: 'Framework' },
        { name: 'OpenAI', category: 'LLM' },
        { name: 'Pinecone', category: 'Vector DB' },
        { name: 'AWS/Firebase', category: 'Cloud' },
        { name: 'Docker', category: 'DevOps' },
        { name: 'FastAPI', category: 'Backend' },
        { name: 'Nicegui', category: 'Frontend' },
        { name: 'HuggingFace', category: 'Models' },
      ]
    },

    roi: {
      title: "Your Return on Time",
      description:
        "You’re not learning to pass interviews. You’re learning to ship products. AI engineers with tangible projects get priority.",
      investment: course2.price,
      salaryIncrease: "Real examples available during webinar",
      roi: "Value depends on your domain and output",
      averageJump: "Developers typically see stronger opportunities after shipping 1-2 real apps",
    },


    success: {
      title: "Professional Success Stories",
      description: "Real transformations from developers like you.",
      stories: [
        {
          name: 'Rahul A',
          before: 'QA Member',
          after: 'AI Engineer',
          company: 'Startup',
          increase: '₹5L → ₹12L',
          image: '',
        },
        {
          name: 'Gomathi',
          before: 'Support Engineer',
          after: 'ML Developer',
          company: 'AI Startup',
          increase: '₹9L → ₹15L',
          image: '',
        },
        {
          name: 'Visweshwar',
          before: 'Full Stack Dev',
          after: 'GenAI Developer',
          company: 'MNC',
          increase: '₹5L → ₹9L',
          image: '',
        },
      ]
    },

    investment: {
      leftSide: {
        title: "Investment & Value",
        description: "Everything you need to master production GenAI.",
        features: [
          '8 weeks intensive training',
          'Live evening sessions',
          '2 production-grade projects',
          'Career support & placement',
          'Lifetime community access',
          'Course recordings',
          'Weekly code reviews',
        ],
        comparison: "Compare with 6-month bootcamps costing ₹2L+"
      },
      pricing: {
        badge: 'Limited Seats Available',
        mrp: course2.mrp,
        price: course2.price,
        discountText: "Limited time offer, Register soon to avail the earlier bird offer, an additional 12% discount",
        emi: '15,000 p.m', // You might want to add actual EMI value
        guarantee: '15-Day Guarantee', // Or any other guarantee text
        ctaHref: '/enroll?courseId=2',
        included: [
          '8 weeks intensive training',
          'Live evening sessions',
          '2 production-grade projects',
          'Career support & placement',
          'Lifetime community access',
          'Course recordings',
          'Weekly code reviews',
        ],
      },
    },

    schedule: {
      title: "Built for Working Professionals",
      description: "Flexible schedule designed around your full-time job.",
      items: [
        { icon: FaClock, title: 'Evening Sessions', desc: '8-10 PM IST, perfect for working professionals' },
        { icon: FaCalendar, title: 'Weekend Reviews', desc: 'Code reviews & doubt clearing on Saturdays' },
        { icon: FaVideo, title: 'All Recordings', desc: 'Miss a session? Watch recordings anytime' },
        { icon: FaLaptop, title: 'Flexible Learning', desc: 'Balance work and upskilling seamlessly' },
      ]
    },

    faq: {
      title: "Frequently Asked Questions",
      description: "Everything you need to know before enrolling.",
      faqs: [
        {
          question: 'Do I need to know Python?',
          answer: 'Yes, intermediate Python knowledge is required. We provide a refresher in weeks 1-2.',
        },
        {
          question: 'Can I do this while working full-time?',
          answer: 'Absolutely! Sessions are scheduled 8-10 PM IST, and all recordings are available.',
        },
        {
          question: 'What if I miss sessions?',
          answer: 'All sessions are recorded and available within 24 hours. Plus weekend reviews cover everything.',
        },
        {
          question: 'What tech background do I need?',
          answer: 'Software development experience with Python, basic ML understanding helps but not mandatory.',
        },
        {
          question: 'Is placement guaranteed?',
          answer: 'We provide career support, resume reviews, and interview prep. Companies are waiting for the resumes of right candidates',
        },
        {
          question: 'How is this different from 6-month bootcamps?',
          answer: 'We focus on experienced developers, skip basics, and dive deep into production systems in 8 weeks.',
        },
      ]
    },

    cta: {
      badge: `Next Batch Starts: ${course2.startDate}`,
      title: "Join the next builder cohort",
      description: `Learn the stack used in actual GenAI engineering. Seats are limited.`,
      enrollButton: {
        text: "Check Eligibility",
        href: "/assessment"
      },
      consultationButton: {
        text: "Talk to Program Lead",
        href: "/contact"
      },
      seatsText: "20 seats • built for working professionals",
    },

    codeverseMentor: {
      badge: 'Exclusive Learning Tool',
      title: 'Accelerate Development with Codeverse Mentor',
      subtitle: 'ChatGPT gives answers. Codeverse Mentor builds production-ready developers.',
      comparisonTable: {
        codeverseColumn: 'Codeverse Mentor',
        otherToolsColumn: 'ChatGPT & Other AI',
        rows: [
          { feature: 'Proactive step-by-step guidance' },
          { feature: 'Real-time code validation' },
          { feature: 'Context-aware assistance' },
          { feature: 'Zero prompt anxiety' },
          { feature: 'Progress tracking with milestones' },
          { feature: 'Builds independence through micro-steps' },
        ],
      },
      featuresTitle: 'How Codeverse Mentor Accelerates Your Learning',
      features: [
        {
          title: 'Production-Ready Project Journey',
          description: 'Advanced projects broken into achievable milestones with clear objectives and validation criteria. Build complex systems step-by-step with confidence.',
        },
        {
          title: 'Real-Time Code Validation',
          description: 'Instant feedback on your code directly in VS Code. Catch issues early, follow best practices, and ensure production-quality code from the start.',
        },
        {
          title: 'Context-Aware AI Assistance',
          description: 'Get help tailored to your specific implementation. Pre-structured prompts for advanced scenarios eliminate guesswork and accelerate development.',
        },
        {
          title: 'Advanced Learning Paths',
          description: 'Master production-grade patterns and architectures used by leading AI companies. Build skills that translate directly to real-world projects.',
        },
        {
          title: 'Smart Debugging Support',
          description: 'Get unstuck on complex issues without losing learning value. Develop problem-solving skills essential for professional development.',
        },
        {
          title: 'Project Milestone Tracking',
          description: 'Track your progress through production-grade projects. Visualize achievement milestones and stay motivated as you build portfolio-worthy applications.',
        },
      ],
      freeAccess: {
        badge: 'Included Free',
        title: 'Free Access for Entire Course Duration',
        description: 'Build production-grade AI applications faster with our proprietary VS Code extension. Available exclusively to all enrolled students at no additional cost.',
        buttonText: 'Learn More About Codeverse Mentor',
        link: 'https://marketplace.visualstudio.com/items?itemName=OjasaMirai.codeverse-mentor',
      },
    },

  };

  return (
    <>
      <Helmet>
        <title>Experienced GenAI Track | Build Production-Ready AI Systems in 8 Weeks</title>
        <meta name="description" content="Advance your career as a developer with our 8-week Experienced GenAI Track. Learn LangChain, RAG pipelines, vector databases, and deploy real-world LLM apps." />
      </Helmet>
      <Box>
        <Navbar />
        <ExperiencedHero data={pageData.hero} />
        <ExperiencedWhoFor data={pageData.whoFor} />
        <ExperiencedCurriculum data={pageData.curriculum} />
        <ExperiencedProjects data={pageData.projects} />
        <ExperiencedTechStack data={pageData.techStack} />
        <CodeverseMentorSection data={pageData.codeverseMentor} />
        <ExperiencedSuccess data={pageData.success} />
        <InvestmentSection data={pageData.investment} />
        <ExperiencedSchedule data={pageData.schedule} />
        <ExperiencedROI data={pageData.roi} />
        <ExperiencedFAQ data={pageData.faq} />
        <ExperiencedCTA data={pageData.cta} />
        <Footer />
      </Box>
    </>
  );
};

export default ExperiencedTrackPage;